<template>
    <div class="pb-10">
        <v-row dense>
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary" />
            </v-col>
            <v-col cols="12" md="8">
                <v-row dense class="pb-4 justify-space-between px-2">
                    <div v-if="!isHotelDescriptionMode" class="text-link" @click="showModal">
                        {{ hotelInfo.contentProviderId === 1 ? `${$t('own_description')}: ` : '' }}
                        {{ getName(hotelInfo.hotelName) }}
                        <v-icon>mdi-link-variant</v-icon>
                    </div>
                    <div class="text-medium-emphasis ml-auto">Hotel Id: {{ hotelInfo.hotelId || '' }}</div>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <TextFieldLocale
                                    v-if="'hotelName' in hotelInfo"
                                    v-model="hotelInfo.hotelName[activeLangCode]"
                                    :label="`${$t('name')}*`"
                                    :active-lang-code="activeLangCode"
                                    data-cy="hotel-name"
                                    :rules="[requiredRule('name')]"
                                    :readonly="
                                        (isHotelDescriptionMode &&
                                            !app.$conf.public.account.allowToEditExternalHotelDescription) ||
                                        (!authStore.isTO1 && contractId !== 0)
                                    "
                                    :disabled="
                                        (isHotelDescriptionMode &&
                                            !app.$conf.public.account.allowToEditExternalHotelDescription) ||
                                        (!authStore.isTO1 && contractId !== 0)
                                    "
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-autocomplete
                                    v-model="hotelInfo.supplierId"
                                    :items="useContentProviderIdItems"
                                    :label="`${$t('source_of_description')}*`"
                                    item-title="name"
                                    item-value="id"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="source-of-description"
                                    :rules="[requiredRule('source_of_description')]"
                                    :disabled="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="hotelInfo.hotelCode"
                                    :label="`${$t('code')}* (${$t('code_rule')})`"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="code"
                                    :rules="[requiredRule('code')]"
                                    :disabled="!!hotelInfo?.hotelId"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-autocomplete
                                    v-model="hotelInfo.hotelChainId"
                                    :items="useHotelChainDictionary"
                                    item-title="name"
                                    item-value="id"
                                    :label="$t('hotel_chain')"
                                    :clearable="
                                        !isHotelDescriptionMode ||
                                        app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                    color="primary"
                                    data-cy="chain-id"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                >
                                    <template v-slot:item="{props, item}">
                                        <v-list-item
                                            v-bind="props"
                                            :prepend-avatar="item.raw.avatar"
                                            :title="item.raw.name || item.raw.id"
                                        />
                                    </template>
                                    <template v-slot:selection="{item}">
                                        {{ item.raw.name || item.raw.id }}
                                    </template>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <v-autocomplete
                                    v-model="hotelInfo.hotelTypeId"
                                    :items="useHotelInfoType"
                                    item-title="name"
                                    item-value="id"
                                    :label="`${$t('type')}${hotelInfo.supplierId !== 1 && isHotelDescriptionMode ? '' : '*'}`"
                                    color="primary"
                                    data-cy="type"
                                    :clearable="hotelInfo.supplierId !== 1 && isHotelDescriptionMode"
                                    :rules="
                                        hotelInfo.supplierId !== 1 && isHotelDescriptionMode
                                            ? []
                                            : [requiredRule('type')]
                                    "
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-autocomplete
                                    v-model="hotelInfo.categoryId"
                                    :items="useCategoryDictionary"
                                    item-title="name"
                                    item-value="id"
                                    :label="`${$t('category')}*`"
                                    color="primary"
                                    data-cy="category"
                                    :rules="[requiredRule('category')]"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <v-autocomplete
                                    ref="option"
                                    v-model="hotelInfo.countryId"
                                    :items="countries"
                                    item-title="title"
                                    item-value="value"
                                    :label="`${$t('country')}*`"
                                    closable-chips
                                    :clearable="
                                        !isHotelDescriptionMode ||
                                        app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                    color="primary"
                                    data-cy="country-id"
                                    :rules="[requiredRule('country')]"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                    @update:model-value="updateCities()"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-autocomplete
                                    ref="cityRef"
                                    v-model="hotelInfo.cityId"
                                    :items="cities"
                                    item-title="title"
                                    item-value="value"
                                    :label="`${$t('city')}*`"
                                    :loading="isCitiesLoading"
                                    :disabled="isCitiesLoading"
                                    closable-chips
                                    :clearable="
                                        !isHotelDescriptionMode ||
                                        app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                    color="primary"
                                    data-cy="city-id"
                                    :rules="[requiredRule('city')]"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                    @update:model-value="updateDistricts()"
                                />
                            </v-col>
                        </v-row>
                        <v-row v-if="false" dense>
                            <v-col cols="12" sm="6">
                                <v-autocomplete
                                    ref="districtRef"
                                    v-model="hotelInfo.districtId"
                                    :items="districts"
                                    item-title="title"
                                    item-value="value"
                                    :label="$t('district')"
                                    :loading="isDistrictsLoading"
                                    :disabled="isDistrictsLoading"
                                    closable-chips
                                    :clearable="
                                        !isHotelDescriptionMode ||
                                        app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                    color="primary"
                                    data-cy="district-id"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="hotelInfo.postalCode"
                                    variant="outlined"
                                    data-cy="zip"
                                    :label="`${$t('postal_code')}*`"
                                    :rules="[requiredRule('postal_code')]"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                        </v-row>
                        <v-row v-if="false" dense>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="hotelInfo.groupNumber"
                                    :label="$t('group_number')"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="group-number"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="hotelInfo.giataId"
                                    label="GIATA ID"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="GIATA"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <phone-field
                                    v-model="hotelInfo.phone"
                                    :label="$t('phone')"
                                    color="primary"
                                    data-cy="phone"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <phone-field
                                    v-model="hotelInfo.phone2"
                                    :label="`${$t('phone')} 2`"
                                    color="primary"
                                    data-cy="second-phone"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <phone-field
                                    v-model="hotelInfo.phone3"
                                    :label="`${$t('phone')} 3`"
                                    color="primary"
                                    data-cy="third-phone"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="hotelInfo.fax"
                                    :label="`${$t('fax')}`"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="fax"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                            <v-col cols="12" sm="8">
                                <v-text-field
                                    v-model="hotelInfo.url"
                                    label="URL"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="url"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="hotelInfo.email"
                                    :label="$t('email')"
                                    type="email"
                                    variant="outlined"
                                    color="primary"
                                    :rules="[emailRule('email')]"
                                    data-cy="email"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="hotelInfo.email2"
                                    :label="`${$t('email')} 2`"
                                    type="email"
                                    variant="outlined"
                                    color="primary"
                                    :rules="[emailRule('email')]"
                                    data-cy="second-email"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="hotelInfo.email3"
                                    :label="`${$t('email')} 3`"
                                    type="email"
                                    variant="outlined"
                                    color="primary"
                                    :rules="[emailRule('email')]"
                                    data-cy="third-email"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                            <v-col cols="12">
                                <TextFieldLocale
                                    v-if="'address' in hotelInfo"
                                    v-model="hotelInfo.address[activeLangCode]"
                                    :label="$t('address')"
                                    :active-lang-code="activeLangCode"
                                    data-cy="address"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                            <v-col v-if="false" cols="12">
                                <TextFieldLocale
                                    v-if="'fullLegalNameLocalizable' in hotelInfo"
                                    v-model="hotelInfo.fullLegalNameLocalizable[activeLangCode]"
                                    :label="$t('company_official_name')"
                                    :active-lang-code="activeLangCode"
                                    data-cy="company-official-name"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                            <v-col v-if="false" cols="12" sm="4">
                                <v-text-field
                                    v-model="hotelInfo.registrationID"
                                    :label="`${$t('registration_id')}`"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="registration-id"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                            <v-col v-if="false" cols="12" sm="4">
                                <v-text-field
                                    v-model="hotelInfo.taxIdentificationNumber"
                                    :label="`${$t('tax_id')}`"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="tax-identification-number"
                                    :readonly="
                                        isHotelDescriptionMode &&
                                        !app.$conf.public.account.allowToEditExternalHotelDescription
                                    "
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-col>
            <v-col v-if="false" cols="12" md="2" class="pl-md-4 pt-md-10">
                <span class="text-h6 text--secondary">
                    {{ $t('logo') }}
                </span>
                <div class="relative-position">
                    <v-img
                        :src="
                            hotelLogo?.imageData && !hotelLogo?.toDelete
                                ? $filters.imageUrl(hotelLogo?.imageData, 250)
                                : logo
                        "
                        :lazy-src="$filters.srcHost('/placeholder.png')"
                        max-height="180"
                        max-width="300"
                    />
                    <v-row
                        v-if="!isHotelDescriptionMode && !app.$conf.public.account.allowToEditExternalHotelDescription"
                        dense
                        class="pt-2"
                    >
                        <div class="ml-auto width-max-content">
                            <v-file-input
                                v-model="file"
                                prepend-icon="mdi-pencil"
                                small-chips
                                variant="outlined"
                                accept="image/jpg, image/jpeg, image/png"
                                hide-details
                                :label="$t('select_photo')"
                                data-cy="add-logo"
                                class="hide-input"
                                @update:model-value="loadFile"
                            />
                        </div>
                        <v-icon
                            icon="mdi-close"
                            color="error"
                            data-cy="remove-logo"
                            class="cursor-pointer ml-2"
                            @click="removeFile"
                        />
                    </v-row>
                </div>
            </v-col>
        </v-row>
        <ModalWrapper
            v-model="isShowModal"
            scrollable
            :max-width="1200"
            hide-cancel
            :persistent="isCreateMode && !hotelInfo.hotelContentId"
        >
            <template v-slot:title>
                {{ $t('associate_with') }}
            </template>
            <template v-slot:content>
                <v-row v-if="!isCopyingHotelInfo" dense class="pb-4">
                    <ProductHotelsDescriptionsFilter
                        v-model="descriptionFilters"
                        :has-active-filters="hasActiveDescriptionFilters"
                        :is-hide-inactive-filter="true"
                        :is-show-full-list-of-content-providers="true"
                        @submit="loadHotels()"
                        @reset="resetDescriptionFilters"
                    />
                    <v-data-table-server
                        :headers="useHotelHeaders"
                        :items="hotels"
                        :items-per-page-options="useTableOptions(totalHotelsCount)"
                        :page="tablePage"
                        :items-per-page="tableItemsPerPage"
                        :loading="isHotelsLoading"
                        :items-length="totalHotelsCount"
                        disable-sort
                        density="compact"
                        color="primary"
                        class="elevation-1 table-server"
                        data-cy="hotels-table"
                        @update:page="onPageUpdate"
                        @update:items-per-page="onItemsPerPageUpdate"
                    >
                        <template v-slot:item.name="{item}">
                            <div class="text-link" :data-cy="`hotel-${item.id}`" @click="associateHotel(item)">
                                {{ item.name }}
                            </div>
                        </template>
                        <template v-slot:item.contentProviderId="{item}">
                            {{
                                item.contentProviderId === 1
                                    ? $t('own_description')
                                    : getContentProviderName(item.contentProviderId)
                            }}
                        </template>
                    </v-data-table-server>
                </v-row>
                <v-row v-else dense class="pb-4">
                    <p>{{ $t('copying_data') }}</p>
                    <v-progress-linear color="primary" indeterminate class="mb-0" height="3px" />
                </v-row>
            </template>
            <template v-slot:btns>
                <v-spacer />
                <v-btn
                    data-cy="cancel"
                    :disabled="isCopyingHotelInfo"
                    color="inherit"
                    @click="isCreateMode && !hotelInfo.hotelContentId ? backToTable() : (isShowModal = false)"
                >
                    {{ $t('cancel') }}
                </v-btn>
            </template>
        </ModalWrapper>
    </div>
</template>

<script setup lang="ts">
    import TextFieldLocale from '~/components/TextFieldLocale.vue'
    import {requiredRule, emailRule} from '~/composables/rules'
    import PhoneField from '~/components/snippets/forms/PhoneField.vue'
    import {useCategoryDictionary} from '~/composables/form-helpers'
    import type {SelectOptionType} from '~/utils/types/types'
    import ProductHotelsDescriptionsFilter from '~/components/products/hotels/ProductHotelsDescriptionsFilter.vue'
    import type {AccommodationDescriptionFilterFilterType} from '~/utils/types/account/products/types'
    import {initAccommodationDescriptionFilter} from '~/utils/types/account/products/helpers'
    import {CHANGE_LOCALE_EVENT, EventBus} from '~/utils/event-bus'
    import {useTableOptions} from '~/composables/helpers'
    import {blobToBase64, clone, clean, isEqual} from '~/utils/helpers'

    const props = defineProps<{
        activeLangCode: string
        associateWithHotel?: boolean
    }>()

    const emit = defineEmits<{
        (e: 'updateContract' | 'cancel'): void
        (e: 'addAssociateHotelId', value: string | number): void
    }>()

    const isCitiesLoading = ref<boolean>(false)
    const isCountriesLoading = ref<boolean>(false)
    const cities = ref<SelectOptionType[]>([])
    const countries = ref<SelectOptionType[]>([])
    const defaultCountryId = ref<number | null>(null)
    const cityRef = ref()
    const isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)
    const isShowModal = ref<boolean>(false)
    const descriptionFilters = ref<AccommodationDescriptionFilterFilterType>(initAccommodationDescriptionFilter())
    const isHotelsLoading: Ref<boolean> = ref(false)
    const hotels = ref([])
    const totalHotelsCount = ref<number>(0)
    const tablePage = ref<number>(1)
    const tableItemsPerPage = ref<number>(10)
    const contractId: number | null = inject('contractId')
    const isCreateMode = ref<boolean>(false)
    const isDistrictsLoading = ref<boolean>(false)
    const districts = ref<SelectOptionType[]>([])
    const districtRef = ref()
    const logo = ref(null)
    const hotelLogo = ref(null)
    const file = ref(null)
    const isCopyingHotelInfo = ref<boolean>(false)

    const hotelInfo = defineModel<any>()

    const app = useNuxtApp()
    const {t} = useI18n()
    const supplierStore = useSupplierStore()
    const authStore = useAuthStore()

    onMounted(async () => {
        if (contractId === 0 && !isHotelDescriptionMode) {
            isCreateMode.value = true
            if (props.associateWithHotel) {
                isShowModal.value = true
            } else {
                //clear supp hotel info.
                await supplierStore.clearAllContractInfo()
                await supplierStore.loadHotelData()
                await loadCountries()
                hotelInfo.value.hotelName = {}
                // Default country: Greece, resolved by ISO2/name after loadCountries
                if (defaultCountryId.value) {
                    hotelInfo.value.countryId = defaultCountryId.value
                    await loadCities()
                }
                // try save
            }
            loadHotels()
        }

        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
    })

    onBeforeUnmount(async () => {
        EventBus.$off(CHANGE_LOCALE_EVENT, reload)
    })

    async function reload(): Promise<void> {
        await supplierStore.loadHotelData()
    }

    watch(
        () => hotelInfo.value?.hotelId,
        async (): Promise<void> => {
            if (hotelInfo.value.hotelId) {
                countries.value = []
                cities.value = []
                districts.value = []
                await loadCountries()
                await loadHotelImages()
            }
        },
        {deep: true, immediate: true}
    )

    watch(countries, async () => {
        if (hotelInfo.value.countryId) await loadCities()
        if (hotelInfo.value.cityId) await loadDistricts()
    })

    const useContentProviderIdItems = computed(() => {
        return [{name: t('own_description'), id: 1}, ...supplierStore.contentProviders]
    })

    async function loadHotels(): Promise<void> {
        try {
            const itemsPerPage = tableItemsPerPage.value
            const page = tablePage.value
            isHotelsLoading.value = true
            const rq = {
                ...descriptionFilters.value,
                ...(descriptionFilters.value.name && {namePattern: descriptionFilters.value.name}),
            }
            clean(rq)
            if (itemsPerPage !== -1) Object.assign(rq, {limit: itemsPerPage, offset: itemsPerPage * (page - 1)})
            const res = await app.$api.hotels.getFull(rq)
            hotels.value = res.hotels
            totalHotelsCount.value = res.count
        } catch (e) {
            hotels.value = []
            totalHotelsCount.value = 0
        } finally {
            isHotelsLoading.value = false
        }
    }

    const onPageUpdate = async page => {
        tablePage.value = page
        await loadHotels()
    }

    const onItemsPerPageUpdate = async itemsPerPage => {
        tableItemsPerPage.value = itemsPerPage
        await loadHotels()
    }

    const updateCities = async () => {
        if (hotelInfo.value.districtId) {
            districtRef.value.reset()
            hotelInfo.value.districtId = null
        }
        if (hotelInfo.value.cityId) {
            cityRef.value.reset()
            hotelInfo.value.cityId = null
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        hotelInfo.value.countryId ? await loadCities() : (cities.value = [])
    }

    async function loadCountries() {
        try {
            isCountriesLoading.value = true
            // zsh: always use /api/locations to keep country naming consistent before and after save.
            // /api/contentProviders/{id}/locations returns names in the provider's own locale (e.g. "Grèce"),
            // which diverged from the initial "Greece" once the hotel got a supplierId post-save.
            const rs = await app.$api.locations.get({
                limitCountries: 300,
                orderBy: 'ALPHABET',
                applyRestriction: false,
            })
            if (rs.countries) {
                const validCountries = rs.countries.filter(item => item && item.id && item.name)
                countries.value = validCountries.map(({id, name}) => ({title: name, value: id}))
                defaultCountryId.value =
                    validCountries.find(item => item.ISO2Code === 'GR')?.id ??
                    validCountries.find(item => item.name?.toLowerCase() === 'greece')?.id ??
                    null
            }
        } catch (e) {
            countries.value = []
            defaultCountryId.value = null
        } finally {
            isCountriesLoading.value = false
        }
    }

    async function loadCities() {
        try {
            isCitiesLoading.value = true
            // zsh: always use /api/locations (see loadCountries for the locale-consistency rationale).
            const rs = await app.$api.locations.get({
                limitCities: -1,
                orderBy: 'POPULARITY',
                countryId: hotelInfo.value.countryId,
                contentProviderId: 1,
            })
            if (rs.cities) {
                cities.value = rs.cities
                    .filter(item => item && item.id && item.name)
                    .map(({id, name}) => ({title: name, value: id}))
            }
        } catch (e) {
            cities.value = []
        } finally {
            isCitiesLoading.value = false
        }
    }

    const updateDistricts = async () => {
        if (hotelInfo.value.districtId) {
            districtRef.value.reset()
            hotelInfo.value.districtId = null
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        hotelInfo.value.cityId ? await loadDistricts() : (districts.value = [])
    }

    async function loadDistricts() {
        try {
            isDistrictsLoading.value = true
            const rs = await app.$api.locations.get({
                limitDistricts: -1,
                orderBy: 'POPULARITY',
                parentId: hotelInfo.value.cityId,
                contentProviderId: supplierStore.hotelInfo?.supplierId || 1,
            })
            // Filter out invalid entries and map to proper format
            districts.value = rs.districts
                .filter(item => item && item.id && item.name)
                .map(({id, name}) => ({title: name, value: id}))
        } catch (e) {
            districts.value = []
        } finally {
            isDistrictsLoading.value = false
        }
    }

    const getName = (name): string => {
        if (!name) return ''
        if (name?.en) return name.en
        const firstAvailableKey = Object.keys(name)[0]
        return name[firstAvailableKey]
    }

    const hasActiveDescriptionFilters = computed(() => {
        return !isEqual(initAccommodationDescriptionFilter(), descriptionFilters.value)
    })

    const resetDescriptionFilters = (): void => {
        descriptionFilters.value = initAccommodationDescriptionFilter()
        loadHotels()
    }

    const showModal = () => {
        if (isCopyingHotelInfo.value) isCopyingHotelInfo.value = false
        loadHotels()
        isShowModal.value = true
    }

    const useHotelHeaders = computed(() => {
        return [
            {title: t('name'), value: 'name', minWidth: '120px'},
            {title: t('source_of_description'), value: 'contentProviderId'},
            {title: t('category_suppl'), value: 'category'},
            {title: t('country'), value: 'location.countryName'},
            {title: t('city'), value: 'location.cityName'},
        ]
    })

    const useHotelInfoType = computed(() => {
        // Filter to only show "Hotel" (ID: 27) and "Resort" (ID: 133)
        const allowedTypeIds = [27, 133]
        return (supplierStore?.hotelTypesDictionary || []).filter(type => allowedTypeIds.includes(type.id))
    })

    const useHotelChainDictionary = computed(() => {
        return supplierStore?.hotelChainDictionary
    })

    const associateHotel = async hotel => {
        let hotelContentId = hotel.id
        if (!isCreateMode.value) {
            const fullContract = clone(supplierStore.fullContract)
            delete fullContract.address
            delete fullContract.cityName
            delete fullContract.cityId
            delete fullContract.countryName
            delete fullContract.countryId
            delete fullContract.ratingStars

            await app.$api.ownAccommodationContracts.put({...fullContract, hotelContentId})
            emit('updateContract')
            isShowModal.value = false
        } else {
            const isOwnDescription = hotel.contentProviderId === 1
            if (!isOwnDescription) {
                try {
                    isCopyingHotelInfo.value = true
                    const res = await app.$api.copyHotelInfo.post(hotelContentId)
                    hotelContentId = res.hotelDescriptionInfo.hotelId
                } catch (e) {
                    hotelContentId = null
                    app.$toast.error(t('error_message.copying_error'))
                    isCopyingHotelInfo.value = false
                }
            }
            if (hotelContentId) {
                emit('addAssociateHotelId', hotelContentId)
                await supplierStore.loadHotelInfo(hotelContentId)
                isShowModal.value = false
            }
        }
    }

    const backToTable = () => {
        isShowModal.value = false
        emit('cancel')
    }

    async function loadHotelImages() {
        try {
            const {images} = await app.$api.hotelDescriptionImages.get(supplierStore.hotelInfo?.hotelId, {
                imageType: 'LOGO',
            })
            if (images?.length) hotelLogo.value = images[0]
        } catch (e) {
            app.$toast.error(t('error_message.load_error'))
        }
    }

    const loadFile = async () => {
        if (file.value) {
            logo.value = await blobToBase64(file.value)
            if (hotelLogo.value?.imageData) {
                hotelLogo.value.toDelete = true
            }
        } else {
            logo.value = null
        }
    }

    const removeFile = async () => {
        if (logo.value) logo.value = null
        if (hotelLogo.value?.imageData) {
            hotelLogo.value.toDelete = true
        }
    }

    const getContentProviderName = contentProviderId => {
        return supplierStore.contentProviders?.find(({id}) => id === contentProviderId).name
    }

    defineExpose({
        hotelLogo,
        logo,
    })
</script>

<style scoped>
    .custom-disabled {
        /* Make it look disabled */
        opacity: 0.6 !important;
        cursor: not-allowed !important;
    }

    .custom-disabled :deep(.v-field) {
        /* Disabled field appearance */
        background-color: rgba(0, 0, 0, 0.06) !important;
        color: rgba(0, 0, 0, 0.38) !important;
    }

    .custom-disabled :deep(.v-field__input) {
        /* Disabled input text */
        color: rgba(0, 0, 0, 0.38) !important;
        cursor: not-allowed !important;
    }

    .custom-disabled :deep(.v-field__append-inner) {
        /* Disabled dropdown arrow */
        color: rgba(0, 0, 0, 0.38) !important;
        cursor: not-allowed !important;
    }

    .custom-disabled :deep(.v-field__outline) {
        /* Disabled border */
        border-color: rgba(0, 0, 0, 0.38) !important;
    }

    .custom-disabled :deep(.v-label) {
        /* Disabled label */
        color: rgba(0, 0, 0, 0.38) !important;
    }

    /* Prevent any interaction while still allowing hover for tooltip */
    .custom-disabled :deep(.v-field__input),
    .custom-disabled :deep(.v-field__append-inner),
    .custom-disabled :deep(.v-field__prepend-inner) {
        cursor: not-allowed !important;
    }
</style>
